import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import {
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import {
    ClientEmployeeIdentifier,
    ClientIdentifierType,
    ClientInformationSources,
} from 'app/modules/shared/interfaces/client-data';
import { ClientsService } from 'app/modules/shared/services/clients.service';

@Component({
    selector: 'client-edit-identities',
    templateUrl: './client-edit-identities.component.html',
    styleUrls: [
        '../../client-edit-screen.component.scss',
        './client-edit-identities.component.scss',
    ],
})
export class ClientEditIdentitiesComponent implements OnInit {
    initControlsFlag: boolean = false;
    clientIdentifierTypes: ClientIdentifierType[];
    clientIdentifierTypesNonEntry: ClientEmployeeIdentifier[];
    currentEmployeeIdentifiers: ClientEmployeeIdentifier[] = [];

    @Input() form: UntypedFormGroup;
    @Input() clientInfoSources: ClientInformationSources;
    @Input() action: string;
    @Input() clientKey: number;
    @Output() formGroupEvent = new EventEmitter<UntypedFormGroup>();

    identifiers: UntypedFormGroup;
    idNMLS: UntypedFormControl;
    idAnalytics: UntypedFormControl;
    idHmdaLEI: UntypedFormControl;
    employeeIdentifiers: UntypedFormArray;

    constructor(private clientsService: ClientsService, private formBuilder: UntypedFormBuilder) {}

    ngOnInit() {
        this.getPageData();
        this.constructGroup();
    }

    ngOnChanges(changes: SimpleChanges) {
        setTimeout(() => {
            let hasControls =
                changes['form']?.currentValue?.controls?.hasOwnProperty('identifiers');
            if (hasControls) {
                this.initControls();
                this.patchValues();
                this.initControlsFlag = true;
            }

            if (this.clientKey && this.action != 'Add') {
                this.clientsService.getClientIdentifierTypeByClientKey(this.clientKey).subscribe(
                    (data) => {
                        this.currentEmployeeIdentifiers = data;
                        this.patchEmployeeIdentifiers();
                    },
                    (error) => {
                        throw new Error(error);
                    }
                );

                this.clientsService
                    .getClientIdentifierTypeSelectNonEntryByClientKey(this.clientKey)
                    .subscribe(
                        (data) => {
                            this.clientIdentifierTypesNonEntry = data;
                        },
                        (error) => {
                            throw new Error(error);
                        }
                    );
            } else {
                this.clientsService.getClientIdentifierType().subscribe(
                    (data) => {
                        this.currentEmployeeIdentifiers = data.slice(0, 1);
                        this.currentEmployeeIdentifiers[0].isPrimaryForEmployeeIdentifier = true;
                        this.currentEmployeeIdentifiers[0].integrationSourceDesc = 'Default';
                        this.currentEmployeeIdentifiers.forEach((identifier) => {
                            identifier.isActive = true;
                        });
                        this.clientIdentifierTypesNonEntry = data.slice(1, data.length);
                        this.patchEmployeeIdentifiers();
                    },
                    (error) => {
                        throw new Error(error);
                    }
                );
            }
        }, 0);
    }

    constructGroup() {
        let group: UntypedFormGroup = this.formBuilder.group({
            idHmdaLEI: ['', Validators.maxLength(20)],
            idNMLS: ['', Validators.maxLength(12)],
            idAnalytics: ['', Validators.maxLength(20)],
            employeeIdentifiers: this.formBuilder.array([]),
        });

        this.formGroupEvent.emit(group);
    }

    initControls() {
        this.identifiers = this.form.get('identifiers') as UntypedFormGroup;
        this.idNMLS = this.identifiers.get('idNMLS') as UntypedFormControl;
        this.idAnalytics = this.identifiers.get('idAnalytics') as UntypedFormControl;
        this.idHmdaLEI = this.identifiers.get('idHmdaLEI') as UntypedFormControl;
        this.employeeIdentifiers = this.identifiers.get('employeeIdentifiers') as UntypedFormArray;
        this.clientKey = this.form.get('clientDetails').get('clientKey').value;
    }

    getPageData() {
        this.clientsService.getClientIdentifierTypes().subscribe(
            (types) => {
                this.clientIdentifierTypes = types;
            },
            (error) => {
                throw new Error(error);
            }
        );
    }

    patchValues() {
        this.identifiers.patchValue(this.clientInfoSources);
    }

    patchEmployeeIdentifiers() {
        let employeeIdentifierGroup: UntypedFormGroup;

        this.employeeIdentifiers.clear();
        this.currentEmployeeIdentifiers.forEach((employeeIdentifier) => {
            employeeIdentifierGroup = this.buildEmployeeIdentifiersGroup(employeeIdentifier);
            this.employeeIdentifiers.push(employeeIdentifierGroup);
        });
    }

    buildEmployeeIdentifiersGroup(employeeIdentifier: ClientEmployeeIdentifier) {
        return this.formBuilder.group({
            identifierType: [employeeIdentifier.identifierType],
            identifierDescription: [employeeIdentifier.identifierDescription],
            integrationSourceDesc: [employeeIdentifier.integrationSourceDesc, Validators.required],
            isActive: [employeeIdentifier.isActive],
            isPrimaryForEmployeeIdentifier: [employeeIdentifier.isPrimaryForEmployeeIdentifier],
            clientIntegrationSourceKey: [employeeIdentifier.clientIntegrationSourceKey],
        });
    }

    clickIdentifierType(index: any) {
        let selectedIdentity = this.clientIdentifierTypesNonEntry[index];
        let employeeIdentifierGroup = this.buildEmployeeIdentifiersGroup(selectedIdentity);
        this.clientIdentifierTypesNonEntry.splice(index, 1);
        this.employeeIdentifiers.push(employeeIdentifierGroup);
    }

    clickIsPrimary(control: any) {
        if (control._disabled == false && control._checked == false) {
            this.employeeIdentifiers.controls.forEach((employeeIdentifier) => {
                employeeIdentifier.get('isPrimaryForEmployeeIdentifier').setValue(false);
            });
        }
    }
}
